// 📦 Imports
import { useEffect, useState, useCallback } from "react";
import Header from "../ligningsmester_2/components/layout/Header";
import ResultScreen from "./components/layout/ResultScreen"
import EquationBox from "./components/layout/EquationBox"
import SideBar from "./components/layout/SideBar"
import { Helmet } from "react-helmet";
import api from "../../../../api";

// 🎮 EquationGame2 Component
export default function EquationGame2() {
  // 🧠 State management
  const [question, setQuestion] = useState(null);           // Current question object
  const [answer, setAnswer] = useState("");                // User's input
  const [step1Feedback, setStep1Feedback] = useState("");  // Step 1 feedback from server
  const [finalFeedback, setFinalFeedback] = useState("");  // Final feedback from server
  const [hasSubmitted, setHasSubmitted] = useState(false);  // Has user submitted an answer?
  const [hasSubmittedStep1, setHasSubmittedStep1] = useState(false); // Has user submitted first step?
  const [hasOutreached, setHasOutreached] = useState(false);// Did user ask for help?
  const [questionNumber, setQuestionNumber] = useState(1);  // Question counter
  const [elapsedSeconds, setElapsedSeconds] = useState(0);  // Time taken to answer
  const [score, setScore] = useState(0);                    // User score
  const [gameOver, setGameOver] = useState(false);          // Has game ended?
  const [isCleared, setIsCleared] = useState(false);        // Level cleared flag
  const [isClearedOnce, setIsClearedOnce] = useState(false);// Was level cleared in this session?
  const [sessionDifficulty, setSessionDifficulty] = useState(null); // Difficulty for current session
  const [startTime, setStartTime] = useState(null);         // Time question was shown
  const [step1Answer, setStep1Answer] = useState(null); // First step answer used for control render phase
  const [levelProgress, setLevelProgress] = useState([]);
  const [shouldEndGame, setShouldEndGame] = useState(false); // ⏳ Track when we should end the game
  const [hasLoggedGame, setHasLoggedGame] = useState(false);

  // Fetch profile data
  useEffect(() => {
    api.get("user/get_level_progress/")
      .then((res) => {
        console.log("Level progress:", res.data);
        setLevelProgress(res.data);
      })
      .catch((err) => {
        console.error("Error fetching level progress:", err);
      });
  }, []);

  // ✅ End game only after levelProgress has updated
  useEffect(() => {
    if (shouldEndGame && levelProgress.length > 0) {
      setGameOver(true);
      setShouldEndGame(false); // 🔄 Reset flag
    }
  }, [shouldEndGame, levelProgress]);

  // 🚀 Fetch new question from backend
  const getNewQuestion = useCallback(async (difficulty = sessionDifficulty) => {
    if (!difficulty) return;
    const res = await api.get(`ligningsmester/level2/generate/?difficulty=${difficulty}`);
    const data = res.data;
    console.log("Fetching new question...", data);
    setQuestion(data);
    setFinalFeedback("");
    setStep1Feedback(null);
    setAnswer("");
    setStartTime(Date.now());
  }, [sessionDifficulty]);

  // 🚨 Submit user's answer
  const submitAnswer = async () => {
    const elapsedSeconds = Math.floor((Date.now() - startTime) / 1000);
    setElapsedSeconds(elapsedSeconds);

    
    const res = await api.post(
      hasSubmittedStep1
        ? "ligningsmester/level2/check_answer/"
        : "ligningsmester/level2/check_step1_answer/",
      {
        question: question.question,
        answer: answer,
        time_taken: elapsedSeconds,
      }
    );
    const data = res.data;
    setScore((prevScore) => prevScore + data.points_awarded);
    if (hasSubmittedStep1) {
      setFinalFeedback(data);
    } else {
      setStep1Feedback(data);
    }
    console.log("score", score);
  };

  // 🎬 Start new game session
  const startGame = useCallback(() => {
    const difficulty = (levelProgress?.find(p => p.game === "ligningsmester" && p.level === 2)?.clears || 0) < 3 ? "easy" : "hard";
    setSessionDifficulty(difficulty);
    setQuestionNumber(1);
    setScore(0);
    setStep1Feedback(null);
    setFinalFeedback(null);
    setHasSubmitted(false);
    setHasSubmittedStep1(false);
    setGameOver(false);
    setIsCleared(false);
    setIsClearedOnce(false);
    setHasLoggedGame(false);
    setTimeout(() => {
      getNewQuestion(difficulty);
    }, 0); // eslint-disable-next-line
  }, [getNewQuestion]);

  // 📥 Log gameprogression
  const logGame = async () => {
    if (hasLoggedGame) return; // Prevent multiple logs
    setHasLoggedGame(true);

    if (isCleared) {
      await api.post("user/track_level_progress/", {
        game: "ligningsmester",
        level: 2,
      });
    }
    // Fetch updated progress BEFORE checking for badge
    const updatedProgress = await api.get("user/get_level_progress/");
    setLevelProgress(updatedProgress.data);

    const clears = updatedProgress.data.find(p => p.game === "ligningsmester" && p.level === 2)?.clears || 0;
    if (clears > 2) {
      console.log("Calling markBadgeEarned");
      markBadgeEarned();
    }
    if (score > 20) {
      console.log("Calling sendXp with:", 95);
      sendXp(95);
    }
  };

  // 📥 Update xp and streak in backend
  const sendXp = async (amount) => {
    const token = localStorage.getItem("access");
    console.log("Sending XP to backend:", amount, "with token:", token);
    await api.post("user/add_xp/", { xp: amount });
  };

  // 🏅 Mark badge earned
  const markBadgeEarned = async () => {
    try {
      const res = await api.post("user/mark-badge-earned/", {
        game: "ligningsmester_2",
        level: 2,
      });
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const data = res.data;
      console.log(data.message);
    } catch (err) {
      console.error("Failed to mark cleared", err);
    }
  };

  // ▶️ Handle next question
  const handleNext = async () => {
    if (questionNumber >= 10) {
      if (!hasLoggedGame) {
        await logGame();
        setHasLoggedGame(true);
      }
      const updatedProgress = await api.get("user/get_level_progress/"); // 🔄 Get updated level progress
      setLevelProgress(updatedProgress.data); // 📊 Store progress
      setShouldEndGame(true); // ⏳ Signal to end game after levelProgress updates
      return;
    }
    setQuestionNumber((prev) => prev + 1);
    getNewQuestion();
    setHasSubmitted(false);
    setHasSubmittedStep1(false);
    setHasOutreached(false);
  };

  // 🧠 Check for level clear
  useEffect(() => {
    if (score > 350 && !isClearedOnce) {
      setIsCleared(true);
      setIsClearedOnce(true);
    }
  }, [score, isClearedOnce]);

  // 🚀 Start game on component mount
  useEffect(() => {
    startGame();
  }, [startGame]);

  // 🔢 Displayed question number (max 10)
  const displayNumber = questionNumber > 10 ? 10 : questionNumber;

  // 🖼️ Render main UI
  return (
    <div className="pageGame">
      <Helmet>
        <title>ligningsmester: kapitel 2 | matly</title>
      </Helmet>
      <Header />

      {/* 📦 Main Content */}
      <div className="contentRow">
        {/* 🔝 Show end-of-game screen*/}
        {gameOver && (
          <ResultScreen
            isCleared={isCleared}
            score={score}
            startGame={startGame}
            levelProgress={levelProgress}
          />
        )}
        {/* 🎯 Equation Box */}
        <EquationBox
          question={question}
          answer={answer}
          setAnswer={setAnswer}
          submitAnswer={submitAnswer}
          hasSubmitted={hasSubmitted}
          setHasSubmitted={setHasSubmitted}
          hasSubmittedStep1={hasSubmittedStep1}
          setHasSubmittedStep1={setHasSubmittedStep1}
          step1Feedback={step1Feedback}
          finalFeedback={finalFeedback}
          handleNext={handleNext}
          questionNumber={questionNumber}
          hasOutreached={hasOutreached}
          setHasOutreached={setHasOutreached}
        />

        {/* 📊 Sidebar */}
        <SideBar
          question={question}
          hasSubmitted={hasSubmitted}
          setHasSubmitted={setHasSubmitted}
          hasSubmittedStep1={hasSubmittedStep1}
          setHasSubmittedStep1={setHasSubmittedStep1}
          hasOutreached={hasOutreached}
          setHasOutreached={setHasOutreached}
          handleNext={handleNext}
          submitAnswer={submitAnswer}
          step1Feedback={step1Feedback}
          finalFeedback={finalFeedback}
          elapsedSeconds={elapsedSeconds}
          sessionDifficulty={sessionDifficulty}
          displayNumber={displayNumber}
          questionNumber={questionNumber}
          step1Answer={step1Answer}
          setStep1Answer={setStep1Answer}
          answer={answer}
          setAnswer={setAnswer}
        />
      </div>
    </div>
  );
}
